// AI Invoice Data Extractor — Uses Lovable AI Gateway via edge function, with local fallback
import { supabase } from "@/integrations/supabase/client";
import { VoucherType } from "@/lib/accounting/types";
import { suggestExpenseCategory } from "./engine";

export interface ExtractedInvoiceData {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  extractedAt: string;
  confidence: number;
  status: "pending" | "approved" | "rejected" | "modified";

  // Extracted fields
  documentType?: string;
  vendor: string;
  vendorGSTIN?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  totalAmount: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalGST: number;
  gstRate: number;
  hsnCode?: string;
  description: string;
  category: string;
  categoryConfidence: number;

  // Suggested accounting entry
  suggestedVoucherType: VoucherType;
  suggestedDebitLedger: string;
  suggestedDebitGroup: string;
  suggestedCreditLedger: string;
  suggestedCreditGroup: string;
  narration: string;

  // AI source
  aiPowered: boolean;

  // Review
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string || "");
    reader.onerror = () => resolve("");
    reader.readAsText(file);
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

async function extractViaAI(file: File): Promise<ExtractedInvoiceData | null> {
  try {
    // Read file content for AI analysis
    let fileContent = "";
    if (file.type === "application/pdf" || file.type.includes("text") || file.type.includes("document")) {
      fileContent = await readFileAsText(file);
    }

    const { data, error } = await supabase.functions.invoke("extract-invoice", {
      body: {
        fileName: file.name,
        fileType: file.type,
        fileContent: fileContent.substring(0, 3000),
      },
    });

    if (error) {
      console.error("Edge function error:", error);
      return null;
    }

    if (data?.error) {
      console.error("AI extraction error:", data.error);
      return null;
    }

    const extracted = data?.data;
    if (!extracted) return null;

    return {
      id: `ai-${crypto.randomUUID().slice(0, 8)}`,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      extractedAt: new Date().toISOString(),
      confidence: extracted.confidence || 0.85,
      status: "pending",
      documentType: extracted.documentType,
      vendor: extracted.vendor,
      vendorGSTIN: extracted.vendorGSTIN,
      invoiceNumber: extracted.invoiceNumber,
      invoiceDate: extracted.invoiceDate,
      dueDate: extracted.dueDate,
      totalAmount: extracted.totalAmount,
      taxableAmount: extracted.taxableAmount,
      cgst: extracted.cgst || 0,
      sgst: extracted.sgst || 0,
      igst: extracted.igst || 0,
      totalGST: extracted.totalGST,
      gstRate: extracted.gstRate,
      hsnCode: extracted.hsnCode,
      description: extracted.description,
      category: extracted.category,
      categoryConfidence: extracted.confidence || 0.85,
      suggestedVoucherType: extracted.suggestedVoucherType as VoucherType,
      suggestedDebitLedger: extracted.suggestedDebitLedger,
      suggestedDebitGroup: extracted.suggestedDebitGroup,
      suggestedCreditLedger: extracted.suggestedCreditLedger,
      suggestedCreditGroup: extracted.suggestedCreditGroup,
      narration: extracted.narration,
      aiPowered: true,
    };
  } catch (err) {
    console.error("AI extraction failed:", err);
    return null;
  }
}

function getCategoryToLedgerMapping(category: string) {
  const mappings: Record<string, { debitLedger: string; debitGroup: string; creditLedger: string; creditGroup: string }> = {
    "Office": { debitLedger: "Office Supplies", debitGroup: "Indirect Expenses", creditLedger: "Accounts Payable", creditGroup: "Sundry Creditors" },
    "Travel": { debitLedger: "Travel Expenses", debitGroup: "Indirect Expenses", creditLedger: "Accounts Payable", creditGroup: "Sundry Creditors" },
    "Utilities": { debitLedger: "Utility Expenses", debitGroup: "Indirect Expenses", creditLedger: "Accounts Payable", creditGroup: "Sundry Creditors" },
    "Marketing": { debitLedger: "Marketing Expenses", debitGroup: "Indirect Expenses", creditLedger: "Accounts Payable", creditGroup: "Sundry Creditors" },
    "Miscellaneous": { debitLedger: "Miscellaneous Expenses", debitGroup: "Indirect Expenses", creditLedger: "Accounts Payable", creditGroup: "Sundry Creditors" },
  };
  return mappings[category] || mappings["Miscellaneous"];
}

async function extractFromDocumentText(file: File): Promise<ExtractedInvoiceData> {
  const text = await readFileAsText(file);
  const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");

  // Extract any numbers or dates with basic regex if present
  const amountMatch = text.match(/(?:total|amount|inr|rs\.?|₹)\s*[:=]?\s*([0-9,]+(?:\.[0-9]{2})?)/i);
  const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, "")) : 0;
  
  const gstinMatch = text.match(/[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}/i);
  const vendorGSTIN = gstinMatch ? gstinMatch[0].toUpperCase() : undefined;

  const invNumMatch = text.match(/(?:inv(?:oice)?|bill)\s*(?:no\.?|num(?:ber)?|#)?\s*[:=]?\s*([A-Za-z0-9\-_/]+)/i);
  const invoiceNumber = invNumMatch ? invNumMatch[1] : `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`;

  const ledgerMap = getCategoryToLedgerMapping("Miscellaneous");
  const today = new Date().toISOString().split("T")[0];

  return {
    id: `ai-${crypto.randomUUID().slice(0, 8)}`,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    extractedAt: new Date().toISOString(),
    confidence: amount > 0 ? 0.7 : 0.4,
    status: "pending",
    vendor: cleanName,
    vendorGSTIN,
    invoiceNumber,
    invoiceDate: today,
    dueDate: today,
    totalAmount: amount,
    taxableAmount: amount > 0 ? Math.round((amount / 1.18) * 100) / 100 : 0,
    cgst: amount > 0 ? Math.round(((amount - amount / 1.18) / 2) * 100) / 100 : 0,
    sgst: amount > 0 ? Math.round(((amount - amount / 1.18) / 2) * 100) / 100 : 0,
    igst: 0,
    totalGST: amount > 0 ? Math.round((amount - amount / 1.18) * 100) / 100 : 0,
    gstRate: 18,
    description: `Document draft: ${cleanName}`,
    category: "Office",
    categoryConfidence: 0.5,
    suggestedVoucherType: "Purchase",
    suggestedDebitLedger: ledgerMap.debitLedger,
    suggestedDebitGroup: ledgerMap.debitGroup,
    suggestedCreditLedger: cleanName,
    suggestedCreditGroup: ledgerMap.creditGroup,
    narration: `Draft purchase voucher from ${cleanName}`,
    aiPowered: false,
  };
}

export async function extractInvoiceData(file: File): Promise<ExtractedInvoiceData> {
  const aiResult = await extractViaAI(file);
  if (aiResult) return aiResult;

  return extractFromDocumentText(file);
}
